import { useEffect, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import type { NetSuiteSyncRun, NetSuiteSyncStatus } from 'common-billing-server/types'
import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'

const formatInterval = (intervalMs: number): string =>
    intervalMs % 3_600_000 === 0 ? `every ${intervalMs / 3_600_000} h` : `every ${Math.round(intervalMs / 60_000)} min`

/** The API returns timestamps as JSON strings, so revive before any date arithmetic. */
const asDate = (value: Date | string): Date => (value instanceof Date ? value : new Date(value))

/** An hourly job needs the wall-clock time and how long ago it was to be readable at a glance. */
const formatWhen = (value: Date | string): string => {
    const date = asDate(value)
    return `${formatDateTime(date)} (${formatDistanceToNow(date, { addSuffix: true })})`
}

/** Names the outcome in the label: colour alone must not be what tells an operator to look. */
const runOutcome = (run: NetSuiteSyncRun): { label: string; color: 'success' | 'warning' | 'error' } => {
    if (!run.succeeded) return { label: 'Failed', color: 'error' }
    const warnings = run.result?.warnings.length ?? 0
    if (!warnings) return { label: 'OK', color: 'success' }
    return { label: `${warnings} warning${warnings === 1 ? '' : 's'}`, color: 'warning' }
}

const runSummary = (run: NetSuiteSyncRun): string => {
    if (!run.result) return run.error ?? 'Failed'
    const { customers, appliancesUpserted, licensePurchasesCreated, allocationsCreated } = run.result.imported
    return [
        `${customers.created} created / ${customers.linked} linked / ${customers.updated} updated customers`,
        `${appliancesUpserted} appliances`,
        `${licensePurchasesCreated} license sales, ${allocationsCreated} bound`,
    ].join(' · ')
}

/** Operator view of the NetSuite import: whether it runs, and what recent runs did. */
export const NetSuiteSync = () => {
    const billingApi = useBillingApi()
    const [status, setStatus] = useState<NetSuiteSyncStatus>()
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    const refresh = async () => {
        const operationId = 'fetch-netsuite-sync'
        removeFailedOperation(operationId)
        try {
            setStatus(await billingApi.getNetsuiteSync())
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed fetching NetSuite sync status', error })
        }
        setIsLoading(false)
    }

    useEffect(() => {
        void refresh()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [billingApi])

    const setSyncEnabled = async (syncEnabled: boolean) => {
        const operationId = 'update-netsuite-sync'
        removeFailedOperation(operationId)
        setIsSaving(true)
        try {
            const settings = await billingApi.getGlobalsettings()
            await billingApi.setGlobalsettings({ ...settings, netsuite: { syncEnabled } })
            await refresh()
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed updating NetSuite sync', error })
        }
        setIsSaving(false)
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        )
    }

    const runs = status?.recentRuns ?? []
    const result = runs.find((run) => !!run.result)?.result
    return (
        <Box sx={{ p: 2 }}>
            {errorBanner}
            <FormControlLabel
                control={
                    <Switch
                        checked={!!status?.syncEnabled}
                        disabled={isSaving || !status}
                        onChange={(event) => void setSyncEnabled(event.target.checked)}
                        slotProps={{ input: { 'aria-label': 'Scheduled NetSuite import' } }}
                    />
                }
                label="Scheduled NetSuite import"
            />
            {status?.intervalMs === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    This deployment has no sync schedule configured, so enabling the import here has no effect. Set
                    NETSUITE_SYNC_INTERVAL_MS in the billing deployment to schedule it.
                </Alert>
            )}
            <Table size="small" sx={{ mt: 2, maxWidth: 860 }} data-testid="netsuite-sync-status">
                <TableBody>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'medium', width: 220 }}>
                            Schedule
                        </TableCell>
                        <TableCell>
                            {status?.intervalMs ? formatInterval(status.intervalMs) : 'Not scheduled'}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'medium' }}>
                            NetSuite account
                        </TableCell>
                        <TableCell>{status?.accountUrl ?? 'Not yet used'}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'medium' }}>
                            Last completed (UTC)
                        </TableCell>
                        <TableCell>{status?.lastCompletedAt ? formatWhen(status.lastCompletedAt) : 'Never'}</TableCell>
                    </TableRow>
                    {result && (
                        <>
                            <TableRow>
                                <TableCell component="th" sx={{ fontWeight: 'medium' }}>
                                    Customers
                                </TableCell>
                                <TableCell>
                                    {result.imported.customers.created} created, {result.imported.customers.linked}{' '}
                                    linked, {result.imported.customers.updated} updated ({result.discovered.customers}{' '}
                                    in NetSuite)
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" sx={{ fontWeight: 'medium' }}>
                                    Appliances
                                </TableCell>
                                <TableCell>
                                    {result.imported.appliancesUpserted} imported ({result.discovered.appliances} in
                                    NetSuite)
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" sx={{ fontWeight: 'medium' }}>
                                    License sales
                                </TableCell>
                                <TableCell>
                                    {result.imported.licensePurchasesCreated} created,{' '}
                                    {result.imported.licensePurchasesUpdated} updated,{' '}
                                    {result.imported.allocationsCreated} bound to a serial (
                                    {result.discovered.licenseSales} in NetSuite,{' '}
                                    {result.discovered.licenseSalesWithSerial} with a serial number)
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                </TableBody>
            </Table>

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Recent runs
            </Typography>
            {runs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No import has run on this database yet.
                </Typography>
            ) : (
                <Table size="small" sx={{ minWidth: 720 }} data-testid="netsuite-sync-runs">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 260 }}>Finished (UTC)</TableCell>
                            <TableCell sx={{ width: 100 }}>Outcome</TableCell>
                            <TableCell>Detail</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {runs.map((run) => (
                            <TableRow key={asDate(run.finishedAt).toISOString()}>
                                <TableCell>{formatWhen(run.finishedAt)}</TableCell>
                                <TableCell>
                                    <Chip size="small" {...runOutcome(run)} />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{runSummary(run)}</Typography>
                                    {run.result?.warnings.map((warning) => (
                                        <Typography key={warning} variant="body2" color="warning.main">
                                            {warning}
                                        </Typography>
                                    ))}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    )
}
